import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import MapViewer from '../components/MapViewer';
import { getMaps } from '../services/api';
import { useOrganizationChange } from '../contexts/GlobalContext';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const MapViewerPage = ({ user, onLogout }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [map, setMap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadMap();
  }, [id]);

  // Reload map when organization changes
  useOrganizationChange(() => {
    loadMap();
  }, [id]);

  const loadMap = async () => {
    try {
      const mapsData = await getMaps();
      const foundMap = Array.isArray(mapsData?.results)
        ? mapsData.results.find(m => m.id === parseInt(id))
        : null;

      if (foundMap) {
        setMap(foundMap);
      } else {
        setError('Map not found');
      }
    } catch (error) {
      console.error('Error loading map:', error);
      setError('Error loading map');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/maps');
  };

  if (loading) {
    return (
      <DashboardLayout user={user} onLogout={onLogout}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 200,
          }}
        >
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading map...</Typography>
        </Box>
      </DashboardLayout>
    );
  }

  if (error || !map) {
    return (
      <DashboardLayout user={user} onLogout={onLogout}>
        <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              variant="outlined"
              sx={{ mr: 2 }}
            >
              Back to Maps
            </Button>
            <Typography component="h2" variant="h6">
              Map Not Found
            </Typography>
          </Box>
          <Alert severity="error" sx={{ mt: 2 }}>
            {error || 'The requested map could not be loaded.'}
          </Alert>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout user={user} onLogout={onLogout}>
      <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            variant="outlined"
            sx={{ mr: 2 }}
          >
            Back to Maps
          </Button>
          <Typography component="h2" variant="h6">
            Map: {map.name}
          </Typography>
        </Box>
        <MapViewer map={map} onBack={handleBack} />
      </Box>
    </DashboardLayout>
  );
};

export default MapViewerPage;
